import React from 'react';
import { useTranslation } from 'react-i18next';

import ClockIcon from '@/assets/icons/clock';
import { Badge } from '@/components/Badge';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { useHasSocialWallet } from '@/hooks/use-has-social-wallet';
import { format } from 'date-fns';

import { UserLimitHistory } from '../hooks/types';
import { getLimitHistoryDisplayAmount, getLimitHistoryTranslation } from '../utils/get-limit-history-texts';

type LimitsHistoryCardProps = {
    limitHistoryItem: UserLimitHistory;
};

const formatDateTime = (timestamp: number) => `${format(timestamp, 'MM/dd/yyyy')} AT ${format(timestamp, 'h:mm a')}`;

export const LimitsHistoryCard = ({ limitHistoryItem }: LimitsHistoryCardProps) => {
    const { t } = useTranslation(['rg', 'common', 'self_exclusion']);
    const hasSocialWallet = useHasSocialWallet();

    const timeToFormat = limitHistoryItem.limit_goes_live_at || limitHistoryItem.created_at;
    const formattedDateTime = formatDateTime(timeToFormat);
    const displayAmount = getLimitHistoryDisplayAmount(limitHistoryItem, t);

    // Use PURCHASE type for social wallet deposit limits, otherwise use original type
    const limitType =
        hasSocialWallet && limitHistoryItem.limit_type === 'DEPOSIT' ? 'PURCHASE' : limitHistoryItem.limit_type;

    return (
        <Box mb="s14">
            <Row alignItems="center">
                <Text color={ds.colorToken.text.secondary} variant="labelMedium" mr="s8">
                    {formattedDateTime}
                </Text>
                {limitHistoryItem.confirmed_status === 'PENDING' ? (
                    <Badge
                        label={t('pending')}
                        type="info"
                        textProps={{ textTransform: 'uppercase', color: 'neutral900', lineHeight: 20 }}
                        leftIcon={<ClockIcon />}
                    />
                ) : null}
            </Row>
            <Row justifyContent="space-between" mb="s14">
                <Text>{getLimitHistoryTranslation(t, limitType, limitHistoryItem.duration ?? undefined)}</Text>
                <Text>{displayAmount}</Text>
            </Row>
            <LineSeparator />
        </Box>
    );
};
